import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { drainQueue, queue } from "../lib/api.ts";

/**
 * Tracks whether the server is reachable and how many mutations are waiting.
 *
 * navigator.onLine only knows about the radio, not about whether Tailscale can
 * reach the server, so the queue is retried on a timer as well as on the
 * browser's online event.
 */
export function useConnection() {
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => queue.subscribe((items) => setPending(items.length)), []);

  useEffect(() => {
    let cancelled = false;

    const attempt = async () => {
      if (cancelled || (await queue.size()) === 0) return;
      setSyncing(true);
      try {
        const result = await drainQueue();
        // The sets just sent left the queue: the session has to be read
        // again, or they vanish from the screen until the next poll.
        if (result.sent > 0) void queryClient.invalidateQueries({ queryKey: ["session"] });
        if (!cancelled) setOnline(result.remaining === 0);
      } finally {
        if (!cancelled) setSyncing(false);
      }
    };

    const onOnline = () => {
      setOnline(true);
      void attempt();
    };
    const onOffline = () => setOnline(false);
    const onVisible = () => {
      if (document.visibilityState === "visible") void attempt();
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(() => void attempt(), 15_000);
    void attempt();

    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [queryClient]);

  return { online, pending, syncing };
}
